import { useEffect, useRef, useState } from 'react';
import useErrors from './useErrors';
import useModuleErrorDialog from './useModuleErrorDialog';
import { useEnvStore } from '../env';

function isOwnTriggeredError (wasTriggered, hasError, message) {
    return wasTriggered && hasError && !!message;
}

/*
 * Shows the error if it belongs to our own triggered call, resetting the guard
 * so the next unrelated failure of the same shared action token isn't
 * misattributed to us; otherwise clears whatever this hook was showing.
 */
function syncErrorState (wasTriggeredRef, hasError, errors, setErrorToShow) {
    if (isOwnTriggeredError(wasTriggeredRef.current, hasError, errors.message)) {
        setErrorToShow(errors);
        wasTriggeredRef.current = false;
    } else {
        setErrorToShow({});
    }
}

/*
 * Scopes a shared errorStore action token to "did my own call trigger this,"
 * so multiple callers of the same action (different rows, different hooks on
 * the same page) each only show their own error. Call markTriggered() right
 * before the awaited store call and clearTriggered() right after it resolves
 * (only reached on success, since a failed call's promise never resolves in
 * this codebase's API layer) — that's what lets the guard reset correctly.
 *
 * markTriggered()/clearTriggered() also show/close the app's global progress
 * indicator (envStore.showProgress/closeProgress, rendered once in App.jsx) for
 * the duration of the call. On failure, closeProgress() is already called by
 * useModuleErrorDialog's own effect as soon as the error dialog activates, so
 * there's no separate close needed on the error path here.
 */
function useTriggeredErrorDialog (props) {
    const {
        hook,
        errorObservables,
        serviceErrorSerializer,
    } = props;

    const envStore = useEnvStore();
    const [errorToShow, setErrorToShow] = useState({});
    const wasTriggered = useRef(false);

    const { errors, hasError } = useErrors({
        hook,
        shouldValidateInput: false,
        useTouched: false,
        input: {},
        inputValidator: () => ({}),
        serviceErrorSerializer,
        errorObservables,
    });

    useEffect(() => {
        syncErrorState(wasTriggered, hasError, errors, setErrorToShow);
    }, [errors]);

    const { renderErrorDialog } = useModuleErrorDialog({ errors: errorToShow });

    function markTriggered () {
        wasTriggered.current = true;
        envStore.showProgress();
    }

    function clearTriggered () {
        wasTriggered.current = false;
        envStore.closeProgress();
    }

    return {
        renderErrorDialog,
        markTriggered,
        clearTriggered,
    };
}

export default useTriggeredErrorDialog;
